import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  UserSelect,
  useDeleteWorkspace,
  useTransferOwnership,
  useUpdateWorkspace,
  useWorkspaceMembers,
} from '@/entities/collab';
import type { Workspace } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { can, PERMISSIONS } from '@/shared/lib/permissions';
import { Button, TextAreaField, TextField, useToast } from '@/shared/ui';

import styles from './Admin.module.css';

/** Hand the workspace to another member: owner only, never delegable, exact name confirms. */
function TransferOwnership({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation();
  const members = useWorkspaceMembers(workspace.id);
  const transfer = useTransferOwnership(workspace.id);
  const toast = useToast();
  const [userId, setUserId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState('');
  const candidates = (members.data ?? []).filter((member) => member.role !== 'owner');
  return (
    <form
      className={`${styles.panel} ${styles.danger}`}
      onSubmit={(event) => {
        event.preventDefault();
        if (userId === null) return;
        transfer.mutate(
          { user_id: userId, confirm_name: confirm },
          {
            onSuccess: () => {
              setUserId(null);
              setConfirm('');
              toast.show({ message: t('workspaceAdmin.transferDone') });
            },
          },
        );
      }}
    >
      <h3 className={styles.subheading}>{t('workspaceAdmin.transferTitle')}</h3>
      <p className={styles.note}>{t('workspaceAdmin.transferHint')}</p>
      <div className={styles.field2}>
        <span className={styles.fieldLabel}>{t('workspaceAdmin.transferTo')}</span>
        <UserSelect
          label={t('workspaceAdmin.transferTo')}
          users={candidates}
          value={userId}
          placeholder={t('workspaceAdmin.pickMember')}
          disabled={transfer.isPending}
          onChange={setUserId}
        />
      </div>
      <TextField
        label={t('workspaceAdmin.transferConfirm')}
        value={confirm}
        autoComplete="off"
        onChange={(event) => {
          setConfirm(event.target.value);
        }}
      />
      <FormError error={transfer.error} />
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="danger"
          disabled={userId === null || confirm !== workspace.name || transfer.isPending}
        >
          {t('workspaceAdmin.transferAction')}
        </Button>
      </div>
    </form>
  );
}

/** Who receives boards when an owner leaves and no team leader can take them over. */
function Succession({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation();
  const members = useWorkspaceMembers(workspace.id);
  const update = useUpdateWorkspace(workspace.id);
  const canEdit = can(workspace.permissions, PERMISSIONS.workspaceUpdateSettings);
  const current = workspace.default_board_owner_user_id;
  return (
    <section className={styles.panel} aria-labelledby="succession-title">
      <h3 id="succession-title" className={styles.subheading}>
        {t('workspaceAdmin.successionTitle')}
      </h3>
      <ol className={styles.steps}>
        <li>{t('workspaceAdmin.successionStep1')}</li>
        <li>{t('workspaceAdmin.successionStep2')}</li>
        <li>{t('workspaceAdmin.successionStep3')}</li>
        <li>{t('workspaceAdmin.successionStep4')}</li>
      </ol>
      <div className={styles.field2}>
        <span className={styles.fieldLabel}>{t('workspaceAdmin.defaultBoardOwner')}</span>
        <UserSelect
          label={t('workspaceAdmin.defaultBoardOwner')}
          users={members.data ?? []}
          value={current}
          placeholder={t('workspaceAdmin.defaultBoardOwnerNone')}
          disabled={!canEdit || update.isPending}
          onChange={(userId) => {
            update.mutate({ default_board_owner_user_id: userId });
          }}
        />
      </div>
      {canEdit && current !== null && (
        <div className={styles.actions}>
          <Button
            size="sm"
            variant="ghost"
            disabled={update.isPending}
            onClick={() => {
              update.mutate({ default_board_owner_user_id: null });
            }}
          >
            {t('workspaceAdmin.defaultBoardOwnerClear')}
          </Button>
        </div>
      )}
      <FormError error={update.error} />
    </section>
  );
}

/** Soft deletion with a clear warning and the exact name typed. */
function DeleteWorkspace({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const remove = useDeleteWorkspace(workspace.id);
  const [open, setOpen] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [confirm, setConfirm] = useState('');
  return (
    <section className={`${styles.panel} ${styles.danger}`} aria-labelledby="delete-title">
      <h3 id="delete-title" className={styles.subheading}>
        {t('workspaceAdmin.deleteTitle')}
      </h3>
      <p className={styles.note}>{t('workspaceAdmin.deleteHint')}</p>
      {!open ? (
        <div className={styles.actions}>
          <Button
            variant="danger"
            onClick={() => {
              setOpen(true);
            }}
          >
            {t('workspaceAdmin.deleteStart')}
          </Button>
        </div>
      ) : (
        <form
          className={styles.panel}
          onSubmit={(event) => {
            event.preventDefault();
            remove.mutate(
              { confirm_name: confirm },
              {
                onSuccess: () => {
                  toast.show({ message: t('workspaceAdmin.deleteDone', { name: workspace.name }) });
                  void navigate('/app', { replace: true });
                },
              },
            );
          }}
        >
          <ul className={styles.warnings}>
            <li>{t('workspaceAdmin.deleteWarnBoards')}</li>
            <li>{t('workspaceAdmin.deleteWarnPeople')}</li>
            <li>{t('workspaceAdmin.deleteWarnRecovery')}</li>
          </ul>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={understood}
              onChange={(event) => {
                setUnderstood(event.target.checked);
              }}
            />
            <span>{t('workspaceAdmin.deleteUnderstood')}</span>
          </label>
          <TextField
            label={t('workspaceAdmin.deleteConfirm', { name: workspace.name })}
            value={confirm}
            autoComplete="off"
            onChange={(event) => {
              setConfirm(event.target.value);
            }}
          />
          <FormError error={remove.error} />
          <div className={styles.actions}>
            <Button
              variant="ghost"
              onClick={() => {
                setOpen(false);
                setUnderstood(false);
                setConfirm('');
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="danger"
              disabled={!understood || confirm !== workspace.name || remove.isPending}
            >
              {t('workspaceAdmin.deleteAction')}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

export function SettingsTab({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation();
  const update = useUpdateWorkspace(workspace.id);
  const canEdit = can(workspace.permissions, PERMISSIONS.workspaceUpdateSettings);
  const [name, setName] = useState(workspace.name);
  const [description, setDescription] = useState(workspace.description ?? '');
  const dirty = name.trim() !== workspace.name || description !== (workspace.description ?? '');
  const mayDelete =
    workspace.workspace_type === 'team' &&
    (workspace.is_owner || can(workspace.permissions, PERMISSIONS.workspaceDelete));
  return (
    <div className={styles.panel}>
      <form
        className={styles.panel}
        onSubmit={(event) => {
          event.preventDefault();
          update.mutate({ name: name.trim(), description });
        }}
      >
        <TextField
          label={t('home.workspaceName')}
          value={name}
          maxLength={100}
          disabled={!canEdit}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <TextAreaField
          label={t('workspaceAdmin.description')}
          value={description}
          maxLength={2000}
          disabled={!canEdit}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />
        <FormError error={update.error} />
        {canEdit && (
          <div className={styles.actions}>
            <Button
              type="submit"
              variant="primary"
              disabled={!dirty || update.isPending || !name.trim()}
            >
              {t('common.save')}
            </Button>
          </div>
        )}
      </form>
      <Succession workspace={workspace} />
      {workspace.is_owner && <TransferOwnership workspace={workspace} />}
      {mayDelete && <DeleteWorkspace workspace={workspace} />}
    </div>
  );
}
